
import React from 'react';
import { Menu, RefreshCcw, Bell, Command } from 'lucide-react';
import { User } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { at } from '../../i18nAdmin';

interface AdminHeaderProps {
    user: User | null;
    onMenuClick: () => void;
    onRefresh: () => void;
    onBellClick: () => void;
    title: string;
    hasNewAlerts?: boolean;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({ 
    user, onMenuClick, onRefresh, onBellClick, title, hasNewAlerts = false 
}) => {
    const { lang } = useLanguage();

    return (
        <header className="h-[70px] bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-6 md:px-10 shrink-0 sticky top-0 z-[200]">
            <div className="flex items-center gap-4 flex-1">
                <button 
                    onClick={onMenuClick}
                    className="lg:hidden p-2.5 md:hover:bg-slate-100 rounded-xl text-slate-900 transition-all active:scale-95"
                >
                    <Menu size={20} />
                </button>
                
                <h1 className="text-xl font-black text-slate-900 uppercase tracking-tight hidden sm:block">
                    {at(lang, title)}
                </h1>
            </div>

            <div className="flex items-center gap-3 md:gap-6">
                <button 
                    onClick={onRefresh}
                    className="p-2.5 text-slate-500 md:hover:text-slate-900 md:hover:bg-slate-100 rounded-full transition-all active:rotate-180"
                    title={at(lang, 'refreshPage')}
                >
                    <RefreshCcw size={18} />
                </button>

                <div className="relative">
                    <button 
                        onClick={onBellClick}
                        className="p-2.5 text-slate-500 md:hover:text-slate-900 md:hover:bg-slate-100 rounded-full transition-all active:scale-95"
                    >
                        <Bell size={18} />
                    </button>
                    {hasNewAlerts && (
                        <div className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border-2 border-white animate-pulse" />
                    )}
                </div>

                <div className="w-px h-8 bg-slate-100 mx-1 hidden sm:block" />

                <div className="flex items-center gap-3 group cursor-pointer">
                    <div className="text-right hidden sm:flex flex-col">
                        <span className="text-sm font-black text-slate-900 leading-none md:group-hover:text-rose-500 transition-colors">{user?.name}</span>
                        <span className="text-[10px] font-bold text-slate-500 uppercase mt-1">Super Admin</span>
                    </div>
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-sm ring-1 ring-slate-200 md:group-hover:ring-rose-200 transition-all">
                        <img src={user?.avatar} className="w-full h-full object-cover" alt="" />
                    </div>
                </div>
            </div>
        </header>
    );
};
